// The firm's project list (shared/types.ts Project), read once per app start and shared by every
// page that shows or picks a project; changed online only (Nastavitve → Projekti).
import { useEffect, useSyncExternalStore } from 'react';
import { matchProject, type ProjectMatch } from '../../../shared/projects';
import type { CreateProjectRequest, Project, UpdateProjectRequest } from '../../../shared/types';
import { createProject, deleteProject, getProjects, isFromCache, subscribeAuth, updateProject } from './api';
import { errorText } from '../components/helpers';

export interface ProjectsState {
  /** null until the first answer (also offline without a cached copy: then an empty list arrives). */
  data: Project[] | null;
  loading: boolean;
  error: string | null;
  /** The list came from the offline cache: it cannot be edited now. */
  stale: boolean;
}

let state: ProjectsState = { data: null, loading: false, error: null, stale: false };
let inFlight: Promise<Project[]> | null = null;
let watching = false;
const listeners = new Set<() => void>();

function set(patch: Partial<ProjectsState>): void {
  state = { ...state, ...patch };
  for (const listener of [...listeners]) listener();
}

/** Reads the list again (shared by every caller); resolves with it. */
export function refreshProjects(): Promise<Project[]> {
  if (inFlight) return inFlight;
  set({ loading: true });
  const promise = getProjects().then(
    (list) => {
      set({ data: list, loading: false, error: null, stale: isFromCache(list) });
      return list;
    },
    (err: unknown) => {
      set({ loading: false, error: errorText(err, 'Seznama projektov ni bilo mogoče naložiti.') });
      throw err;
    },
  );
  inFlight = promise;
  const done = () => {
    if (inFlight === promise) inFlight = null;
  };
  promise.then(done, done);
  return promise;
}

/** The list as last read (empty before the first answer). */
export function currentProjects(): Project[] {
  return state.data ?? [];
}

function watch(): void {
  if (watching) return;
  watching = true;
  // Another account, or a login after the first read failed: read again.
  subscribeAuth(() => {
    refreshProjects().catch(() => {});
  });
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const snapshot = () => state;

/** The project list, loaded on first use and kept across pages. */
export function useProjects(): ProjectsState & { reload: () => void } {
  const current = useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => {
    watch();
    if (!current.data && !inFlight) refreshProjects().catch(() => {});
    // Only on mount: the first read is shared, later ones are explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { ...current, reload: () => void refreshProjects().catch(() => {}) };
}

/** The projects a document can be put into: the active ones, plus its own when that is archived. */
export function choosableProjects(list: readonly Project[], currentId: string | null | undefined): Project[] {
  return list.filter((p) => !p.archived || p.id === currentId);
}

/** The project a new text mentions (shared/projects.ts), by the list as last read. */
export function projectForText(text: string): ProjectMatch | null {
  return matchProject(text, currentProjects());
}

// ---------- changes (online only; the store follows the server's answer) ----------

export async function addProject(id: string, req: CreateProjectRequest): Promise<Project> {
  const project = await createProject(id, req);
  const rest = currentProjects().filter((p) => p.id !== project.id);
  set({ data: sortProjects([...rest, project]) });
  return project;
}

export async function changeProject(id: string, req: UpdateProjectRequest): Promise<Project> {
  const project = await updateProject(id, req);
  set({ data: sortProjects(currentProjects().map((p) => (p.id === id ? project : p))) });
  return project;
}

export async function removeProject(id: string): Promise<void> {
  await deleteProject(id);
  set({ data: currentProjects().filter((p) => p.id !== id) });
}

/** The server's order: active first, by name. */
export function sortProjects(list: readonly Project[]): Project[] {
  return [...list].sort((a, b) => Number(a.archived) - Number(b.archived) || a.name.localeCompare(b.name, 'sl') || a.id.localeCompare(b.id));
}

/** For tests: forget the list. */
export function resetProjectsForTests(): void {
  state = { data: null, loading: false, error: null, stale: false };
  inFlight = null;
}
